import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import UploadAdCreativeButton from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/UploadAdCreativeButton'
import SocialAuthChecker from '../../../features/components/SocialAuthChecker'
import SkeletonForm from '../../../components/Form/Skeleton'
import UploadAdCreativeSuccessModal from './UploadAdCreativeSuccessModal'
import FormDrawerWrapper from '../../../features/components/FormDrawerWrapper'
import UploadFacebookAdCreativeForm from './UploadFacebookAdCreativeForm'
import RightWrongDatesMessage from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/RightWrongDatesMessage'

import { ReactComponent as FacebookIcon } from '../../../assets/logos/fb-circle-icon.svg'

import useRedirectFormDrawer from '../../../features/components/FormDrawerWrapper/useRedirectFormDrawer'
import useUploadAdCreativeConstants from '../../Multiplatform/UploadAdCreative/useUploadAdCreativeConstants'

import { redirectTo } from '../../../helpers/url'

import { clearAds } from '../../../modules/actions/ads'
import { clearRightUploadAdData } from '../../../modules/actions/rights'
import { clearAccessibleSocialPages } from '../../../modules/actions/socialAccounts'
import { selectedFacebookAdAccountSelector } from '../../../modules/selectors/adAccounts'
import { clearGetCustomConversions, getCustomConversions } from '../../../modules/actions/choices'
import {
  clearPartnerSelfAccount,
  clearSelfAccount,
  getPartnerSelfAccount,
  getSelfAccount
} from '../../../modules/actions/selfAccounts'
import { customConversionsWasLoadedSelector } from '../../../modules/selectors/choices'
import {
  partnerSelfAccountErrorSelector,
  partnerSelfAccountWasLoadedSelector,
  selfAccountErrorSelector,
  selfAccountWasLoadedSelector
} from '../../../modules/selectors/selfAccounts'
import { formsOpenStatesSelector } from '../../../modules/selectors/forms'
import { selectedPartnershipSelector } from '../../../modules/selectors/partnerships'

import { UPLOAD_FACEBOOK_AD_CREATIVE } from '../../../constants/forms'
import { FACEBOOK_PLATFORM, PLATFORM_LABELS } from '../../../constants/selectLists/platformList'
import { ROUTE_PARAMS, ROUTES } from '../../../constants/routes'
import { MODAL_TYPE } from '../../../constants/other'

const initialRedirectionParams = {
  [ROUTE_PARAMS.campaignId]: '',
  [ROUTE_PARAMS.lineItemId]: ''
}

const UploadFacebookAdCreative = () => {
  const dispatch = useDispatch()

  const [redirectionParams, setRedirectionParams] = useState(initialRedirectionParams)

  const { id: facebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const customConversionsWasLoaded = useSelector(customConversionsWasLoadedSelector)
  const selfAccountWasLoaded = useSelector(selfAccountWasLoadedSelector)
  const selfAccountError = useSelector(selfAccountErrorSelector)
  const partnerSelfAccountWasLoaded = useSelector(partnerSelfAccountWasLoadedSelector)
  const partnerSelfAccountError = useSelector(partnerSelfAccountErrorSelector)
  const { [UPLOAD_FACEBOOK_AD_CREATIVE]: isFormOpen = false } = useSelector(formsOpenStatesSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const partnerAccountId = selectedPartnership.partner
  const rightHolderAccountId = selectedPartnership.rights_holder

  const { isFormLoading: isUploadAdCreativeDataLoading, isRightEndDateInThePast } = useUploadAdCreativeConstants()

  const selfAccountDidLoading = useMemo(
    () => selfAccountWasLoaded || selfAccountError,
    [selfAccountWasLoaded, selfAccountError]
  )
  const partnerSelfAccountDidLoading = useMemo(
    () => partnerSelfAccountWasLoaded || partnerSelfAccountError,
    [partnerSelfAccountWasLoaded, partnerSelfAccountError]
  )

  const isFormLoading = useMemo(
    () =>
      isUploadAdCreativeDataLoading ||
      !customConversionsWasLoaded ||
      !selfAccountDidLoading ||
      !partnerSelfAccountDidLoading,
    [isUploadAdCreativeDataLoading, customConversionsWasLoaded, selfAccountDidLoading, partnerSelfAccountDidLoading]
  )

  const showSuccessModal = useRedirectFormDrawer(
    `${ROUTES.rightsSummary}?message=${MODAL_TYPE.rightFacebookAdUploaded}`
  )

  const redirectToAdSet = useCallback(() => {
    redirectTo(ROUTES.lineItemsSummary, {
      ...redirectionParams,
      [ROUTE_PARAMS.platform]: FACEBOOK_PLATFORM,
      [ROUTE_PARAMS.adAccount]: facebookAdAccountId
    })
  }, [redirectionParams, facebookAdAccountId])

  const onModalCloseHandler = useCallback(() => {
    setRedirectionParams(initialRedirectionParams)
  }, [])

  const onSuccessAdFormSubmit = useCallback(
    params => {
      setRedirectionParams(params)

      dispatch(clearAds())
      showSuccessModal()
    },
    [dispatch, showSuccessModal]
  )

  const ButtonTrigger = useCallback(
    props => <UploadAdCreativeButton Icon={FacebookIcon} text="Facebook and Instagram" {...props} />,
    []
  )

  const onAfterFormCloseHandler = useCallback(() => {
    dispatch(clearRightUploadAdData(FACEBOOK_PLATFORM))
  }, [dispatch])

  useEffect(
    () => () => {
      // We clear accessibleSocialPages on top 'UploadFacebookAdCreative' form component,
      // but we fetch them on CampaignForm level to avoid redundant request in some cases:
      // e.g. facebook platform is not connected, and we don't render CampaignForm
      dispatch(clearAccessibleSocialPages())
    },
    [dispatch]
  )

  useEffect(() => {
    if (isFormOpen) {
      dispatch(
        getCustomConversions({
          account: facebookAdAccountId
        })
      )
    }
  }, [dispatch, isFormOpen, facebookAdAccountId])

  useEffect(() => {
    return () => {
      if (customConversionsWasLoaded) {
        dispatch(clearGetCustomConversions())
      }
    }
  }, [dispatch, customConversionsWasLoaded])

  useEffect(() => {
    if (isFormOpen) {
      dispatch(getSelfAccount(rightHolderAccountId, false))
    }
  }, [dispatch, isFormOpen, rightHolderAccountId])

  useEffect(() => {
    return () => {
      if (selfAccountWasLoaded) {
        dispatch(clearSelfAccount())
      }
    }
  }, [dispatch, selfAccountWasLoaded])

  useEffect(() => {
    if (isFormOpen) {
      dispatch(getPartnerSelfAccount(partnerAccountId))
    }
  }, [isFormOpen, dispatch, partnerAccountId])

  useEffect(() => {
    return () => {
      if (partnerSelfAccountWasLoaded) {
        dispatch(clearPartnerSelfAccount())
      }
    }
  }, [dispatch, partnerSelfAccountWasLoaded])

  return (
    <>
      <FormDrawerWrapper
        formName={UPLOAD_FACEBOOK_AD_CREATIVE}
        closeOnSubmit={false}
        OpenDrawerTrigger={ButtonTrigger}
        onAfterFormClose={onAfterFormCloseHandler}
        isFormLoading={isFormLoading}
        SkeletonFormComponent={SkeletonForm}
      >
        <SocialAuthChecker
          platform={FACEBOOK_PLATFORM}
          authFailureText={`Please authenticate with ${PLATFORM_LABELS[FACEBOOK_PLATFORM]} to upload ad creative.`}
        >
          {isRightEndDateInThePast ? (
            <RightWrongDatesMessage />
          ) : (
            <UploadFacebookAdCreativeForm onSuccessSubmit={onSuccessAdFormSubmit} />
          )}
        </SocialAuthChecker>
      </FormDrawerWrapper>
      <UploadAdCreativeSuccessModal onModalClose={onModalCloseHandler} onGoToAdSet={redirectToAdSet} />
    </>
  )
}

export default UploadFacebookAdCreative
